import { Orders, Order } from '../pages/types';

export const addLeadingZeroToMonth = (month: string) => {
  // Ensure that the month is a number between 1 and 12
  const numericMonth = parseInt(month, 10);
  if (isNaN(numericMonth) || numericMonth < 1 || numericMonth > 12) {
    return '--';
  }

  // Use String.padStart to add a leading zero
  const formattedMonth = numericMonth.toString().padStart(2, '0');
  return formattedMonth;
};

export const getCardSummary = (cardNumber: string, cardType: string, cardDetails: any) => {
  let summary = '';
  let expiration = '';
  if (cardNumber && cardType) {
    summary = `${cardType} x${cardNumber}`;
    if (cardDetails?.exp_month && cardDetails?.exp_year) {
      expiration = `${addLeadingZeroToMonth(cardDetails.exp_month)}/${cardDetails.exp_year}`;
    }
    return `${summary} ${expiration}`;
  }
  return null;
};

export const computeCustomerSpend = (orders: Orders): string => {
  const orderList = [...orders.past, ...orders.processing].filter(
    (pastOrder: any) =>
      pastOrder.status !== 'CANCELLED' &&
      pastOrder.status !== 'HOLD' &&
      pastOrder.status !== 'ERROR'
  );
  const orderSum = sumList(mapOrderToTotalPrice(orderList));

  return Number(orderSum).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const calculateUnitPriceAfterDiscount = (product: any) => {
  if (!product) {
    throw new Error('Product is null or undefined');
  }
  const unitPrice = parseNumber(product.unit_price);
  const priceAfterDiscount =
    unitPrice - parseNumber(product.discount_amount) / parseNumber(product.quantity);
  return priceAfterDiscount.toFixed(2);
};

export const calculatePriceAfterDiscount = (product: any) => {
  if (!product) {
    throw new Error('Product is null or undefined');
  }
  const price = parseNumber(product.unit_price) * parseNumber(product.quantity);
  const priceAfterDiscount = price - parseNumber(product.discount_amount);
  return priceAfterDiscount.toFixed(2);
};

export const calculateAmountRefundedWithoutTax = (
  priceAfterDiscount: any,
  alreadyRefundedAmount: any,
  taxAmount: any
) => {
  const priceAfterDiscountNumber = parseNumber(priceAfterDiscount);
  const alreadyRefundedNumber = parseNumber(alreadyRefundedAmount);
  if (alreadyRefundedNumber === 0) {
    return '0.00';
  }

  const taxAmountNumber = parseNumber(taxAmount);
  const priceCents = Math.round(priceAfterDiscountNumber * 100);
  const fullPriceInclTaxCents = priceCents + Math.round(taxAmountNumber * 100);
  const alreadyRefundedInclTaxCents = Math.round(alreadyRefundedNumber * 100);

  // Only fully refunded when the exact tax-inclusive amounts match.
  if (alreadyRefundedInclTaxCents >= fullPriceInclTaxCents) {
    return priceAfterDiscount;
  }

  const taxRate = taxAmountNumber / priceAfterDiscountNumber;
  const amountRefundedWithoutTax = alreadyRefundedNumber / (1 + taxRate);

  // Round up (never under-report) so availableForRefund can't over-estimate;
  // cap a cent short of full since we've already ruled out truly being done.
  const roundedUpCents = Math.min(Math.ceil(amountRefundedWithoutTax * 100 - 1e-9), priceCents - 1);
  return (roundedUpCents / 100).toFixed(2);
};

export const calculateHalfRefundRoundedUp = (availableForRefund: string) => {
  const availableCents = Math.round(parseNumber(availableForRefund) * 100);
  const halfCents = Math.ceil(availableCents / 2);
  return (halfCents / 100).toFixed(2);
};

export const calculatePriceAfterDiscountWithTax = (product: any) => {
  if (!product) {
    throw new Error('Product is null or undefined');
  }
  const price = parseNumber(product.unit_price) * parseNumber(product.quantity);
  const priceAfterDiscount = price - parseNumber(product.discount_amount);
  const priceWithTax = priceAfterDiscount + parseNumber(product.tax_amount);
  return priceWithTax.toFixed(2);
};

// Rounds the combined tax for a multi-row refund once, then distributes it via
// largest remainder, so the total can't drift from independently rounding each row.
export const allocateRefundTaxAcrossRows = (
  rows: { pricePaid: string; amountToRefund: string; product: any }[]
): string[] => {
  const exactCents = rows.map(({ pricePaid, amountToRefund, product }) => {
    if (parseNumber(product.unit_price) === 0) {
      return 0;
    }
    const taxRate = parseNumber(product.tax_amount) / parseNumber(pricePaid);
    return taxRate * parseNumber(amountToRefund) * 100;
  });

  const flooredCents = exactCents.map((cents) => Math.floor(cents + 1e-9));
  const targetTotalCents = Math.round(exactCents.reduce((sum, cents) => sum + cents, 0));
  const totalFlooredCents = flooredCents.reduce((sum, cents) => sum + cents, 0);
  const centsToDistribute = targetTotalCents - totalFlooredCents;

  const byLargestRemainder = exactCents
    .map((cents, index) => ({ index, remainder: cents - flooredCents[index] }))
    .sort((a, b) => b.remainder - a.remainder);

  const allocatedCents = [...flooredCents];
  for (let i = 0; i < centsToDistribute; i++) {
    allocatedCents[byLargestRemainder[i % byLargestRemainder.length].index] += 1;
  }

  return rows.map(({ product }, index) =>
    parseNumber(product.unit_price) === 0
      ? product.unit_price
      : (allocatedCents[index] / 100).toFixed(2)
  );
};

// Tax for a row refunding everything available: whatever brings its total to
// exactly the full price, instead of the tax rate (which inherits past drift).
export const calculateExactCloseoutTaxAmount = (
  priceAfterDiscount: string,
  taxAmount: string,
  alreadyRefundedInclTax: string,
  amountToRefund: string
): string => {
  const fullPriceInclTaxCents =
    Math.round(parseNumber(priceAfterDiscount) * 100) + Math.round(parseNumber(taxAmount) * 100);
  const alreadyRefundedCents = Math.round(parseNumber(alreadyRefundedInclTax) * 100);
  const remainingInclTaxCents = fullPriceInclTaxCents - alreadyRefundedCents;
  const amountCents = Math.round(parseNumber(amountToRefund) * 100);
  return ((remainingInclTaxCents - amountCents) / 100).toFixed(2);
};

const mapOrderToTotalPrice = (orders: Order[]) =>
  orders.map((order: Order) => parseNumber(order.total_price) - parseNumber(order.total_refunds));

const sumList = (numbersList: number[]): number => {
  const sum = numbersList.reduce((accumulator, currentValue) => {
    return accumulator + currentValue;
  }, 0);

  // Truncate the result to 2 decimal places
  return parseFloat(sum.toFixed(2));
};

export const parseNumber = (number: number | string | null | undefined) => {
  if (number) {
    return Number(number);
  }

  console.warn('Number is null or undefined defaulting to 0');
  return Number('0');
};

export const parseCurrency = (currencyString: string): number => {
  if (!currencyString) return 0;
  const cleanedString = String(currencyString).trim().replace(/[$,]/g, '');
  return parseNumber(cleanedString);
};

/**
 * Formats a price for the BARK product info tables. Prices always show two
 * decimal places; a missing price renders as '--' rather than a misleading 0.00.
 */
export const formatPriceWithCurrency = (
  currency: string,
  price: number | string | null | undefined
): string => {
  if (price === null || price === undefined || price === '') return '--';

  const priceNumber = Number(price);
  if (isNaN(priceNumber)) return '--';

  return `${currency}${priceNumber.toFixed(2)}`;
};
